const FIELD_ALIASES = {
  nome: 'name',
  telefone: 'phone_number',
  celular: 'phone_number',
  site: 'website_url',
  website: 'website_url',
  sobrenome: 'last_name',
  primeiro_nome: 'first_name',
  instagram: 'instagram',
  facebook: 'facebook',
  twitter: 'twitter',
  linkedin: 'linkedin',
  github: 'github',
  youtube: 'youtube',
  line: 'line',
  telegram: 'telegram',
  identificador: 'identifier',
  whatsapp: 'phone_number',
  fb: 'facebook',
  ig: 'instagram',
};

const VALUE_STOP_CHARS = new Set([',', '"', '(', ')', '{', '}']);

class ParseError extends Error {
  constructor(message, position) {
    super(message);
    this.name = 'ParseError';
    this.position = position;
  }
}

export function parse(text) {
  const input = String(text);
  const parser = new Parser(input);
  parser.skipWhitespace();
  parser.parseList();
  return parser.items;
}

export function serialize(items) {
  if (!items || items.length === 0) return '';

  return items
    .map(item => {
      const fields = Object.entries(item.fields)
        .map(([k, v]) => `${k}:${escapeValue(v)}`)
        .join(', ');

      if (item.mode === 'create') {
        return `{${fields}}`;
      }
      if (item.mode === 'reference' && Object.keys(item.fields).length > 1) {
        return `(${fields})`;
      }
      return fields;
    })
    .join(', ');
}

export function validateSyntax(text) {
  try {
    parse(text);
    return { valid: true, errors: [] };
  } catch (e) {
    return {
      valid: false,
      errors: [{ message: e.message, position: e.position }],
    };
  }
}

export function tokenize(text, validFields) {
  const input = String(text || '');
  const tokens = [];
  const validFieldSet = new Set(validFields || []);
  Object.keys(FIELD_ALIASES).forEach(alias => validFieldSet.add(alias));
  Object.values(FIELD_ALIASES).forEach(canonical =>
    validFieldSet.add(canonical)
  );
  let i = 0;

  while (i < input.length) {
    const char = input[i];

    if (/\s/.test(char)) {
      const start = i;
      while (i < input.length && /\s/.test(input[i])) i += 1;
      tokens.push({ type: 'ws', text: input.slice(start, i) });
      continue;
    }

    if (
      char === ',' ||
      char === ':' ||
      char === '(' ||
      char === ')' ||
      char === '{' ||
      char === '}'
    ) {
      tokens.push({ type: 'punct', text: char });
      i += 1;
      continue;
    }

    if (char === '"') {
      const start = i;
      i += 1;
      while (i < input.length) {
        if (input[i] === '"') {
          if (i + 1 < input.length && input[i + 1] === '"') {
            i += 2;
          } else {
            i += 1;
            break;
          }
        } else {
          i += 1;
        }
      }
      tokens.push({ type: 'value', text: input.slice(start, i) });
      continue;
    }

    const start = i;
    while (i < input.length) {
      const c = input[i];
      if (/[\s,(){}:"']/.test(c)) break;
      if (c === '-' && i > start && i + 1 < input.length) {
        const prev = input[i - 1];
        const next = input[i + 1];
        const isLetter = ch => /[a-zA-ZÀ-ÿ]/.test(ch);
        if (isLetter(prev) && isLetter(next)) {
          i += 1;
          continue;
        }
      }
      i += 1;
    }
    const word = input.slice(start, i);

    const prev = tokens[tokens.length - 1];
    const isValue = prev && prev.type === 'punct' && prev.text === ':';

    if (isValue) {
      tokens.push({ type: 'value', text: word });
      continue;
    }

    let j = i;
    while (j < input.length && /\s/.test(input[j])) j += 1;
    const isField = j < input.length && input[j] === ':';

    if (isField) {
      const normalized = normalizeField(word);
      const isValid = validFieldSet.has(normalized);
      tokens.push({
        type: 'field',
        text: word,
        normalized,
        valid: isValid,
      });
    } else {
      tokens.push({ type: 'value', text: word });
    }
  }

  return tokens;
}

function escapeValue(value) {
  if (/[,(){}:"\s]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function normalizeField(raw) {
  const normalized = raw.trim().toLowerCase();
  return FIELD_ALIASES[normalized] || normalized;
}

class Parser {
  constructor(input) {
    this.input = input;
    this.pos = 0;
    this.items = [];
  }

  skipWhitespace() {
    while (this.pos < this.input.length && /\s/.test(this.input[this.pos])) {
      this.pos += 1;
    }
  }

  parseList() {
    if (this.pos >= this.input.length) return;

    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.input.length) break;

      const char = this.input[this.pos];
      if (char === ',') {
        this.pos += 1;
        this.skipWhitespace();
        continue;
      }
      if (char === ')' || char === '}') break;

      const item = this.parseItem();
      if (item) this.items.push(item);

      this.skipWhitespace();
      if (this.pos >= this.input.length) break;

      const nextChar = this.input[this.pos];
      if (nextChar === ',') {
        this.pos += 1;
      } else if (nextChar === ')' || nextChar === '}') {
        break;
      } else {
        throw new ParseError(
          `Esperado ',' ou fim da lista após o item #${this.items.length}`,
          this.pos
        );
      }
    }
  }

  parseItem() {
    this.skipWhitespace();

    const char = this.input[this.pos];
    if (char === '(') return this.parseGroupedReference();
    if (char === '{') return this.parseCreation();
    if (char === '"') {
      throw new ParseError(
        'Item não pode começar com aspas. Use formato campo:valor, (campo:valor) ou {campo:valor}',
        this.pos
      );
    }
    return this.parseReference();
  }

  parseReference() {
    const fieldRaw = this.parseFieldRaw();
    const field = fieldRaw ? normalizeField(fieldRaw) : null;
    if (field === null) {
      throw new ParseError('Nome do campo esperado', this.pos);
    }

    this.skipWhitespace();
    if (this.pos >= this.input.length || this.input[this.pos] !== ':') {
      throw new ParseError(`Esperado ':' após campo '${fieldRaw}'`, this.pos);
    }
    this.pos += 1;

    const value = this.parseValue();

    return { mode: 'reference', fields: { [field]: value } };
  }

  parseGroupedReference() {
    this.pos += 1;
    const fields = this.parseFieldsUntil(')');
    if (this.pos >= this.input.length || this.input[this.pos] !== ')') {
      throw new ParseError("Parêntese não fechado. Esperado ')'", this.pos);
    }
    this.pos += 1;
    return { mode: 'reference', fields };
  }

  parseCreation() {
    this.pos += 1;
    const fields = this.parseFieldsUntil('}');
    if (this.pos >= this.input.length || this.input[this.pos] !== '}') {
      throw new ParseError("Chave não fechada. Esperado '}'", this.pos);
    }
    this.pos += 1;
    return { mode: 'create', fields };
  }

  parseFieldsUntil(closingChar) {
    const fields = {};
    this.skipWhitespace();
    if (this.pos >= this.input.length || this.input[this.pos] === closingChar) {
      return fields;
    }

    while (true) {
      this.skipWhitespace();
      if (this.pos >= this.input.length || this.input[this.pos] === closingChar)
        break;

      const fieldRaw = this.parseFieldRaw();
      const field = fieldRaw ? normalizeField(fieldRaw) : null;
      if (field === null) {
        throw new ParseError('Nome do campo esperado', this.pos);
      }

      this.skipWhitespace();
      if (this.pos >= this.input.length || this.input[this.pos] !== ':') {
        throw new ParseError(`Esperado ':' após campo '${fieldRaw}'`, this.pos);
      }
      this.pos += 1;

      const value = this.parseValue();
      fields[field] = value;

      this.skipWhitespace();
      if (this.pos >= this.input.length || this.input[this.pos] === closingChar)
        break;

      if (this.input[this.pos] !== ',') {
        throw new ParseError(
          `Esperado ',' ou '${closingChar}' após '${field}'`,
          this.pos
        );
      }
      this.pos += 1;
    }

    return fields;
  }

  parseField() {
    this.skipWhitespace();
    if (this.pos >= this.input.length) return null;

    const start = this.pos;
    while (
      this.pos < this.input.length &&
      /[a-zA-Z0-9_\-\u00C0-\u024F\u1E00-\u1EFF]/.test(this.input[this.pos])
    ) {
      this.pos += 1;
    }

    if (this.pos === start) return null;

    const raw = this.input.slice(start, this.pos);
    return normalizeField(raw);
  }

  parseFieldRaw() {
    this.skipWhitespace();
    if (this.pos >= this.input.length) return null;

    const start = this.pos;
    while (
      this.pos < this.input.length &&
      /[a-zA-Z0-9_\-\u00C0-\u024F\u1E00-\u1EFF]/.test(this.input[this.pos])
    ) {
      this.pos += 1;
    }

    if (this.pos === start) return null;
    return this.input.slice(start, this.pos);
  }

  parseValue() {
    this.skipWhitespace();
    if (this.pos >= this.input.length) return '';

    if (this.input[this.pos] === '"') {
      return this.parseQuotedValue();
    }
    return this.parseBareValue();
  }

  parseBareValue() {
    const start = this.pos;
    while (
      this.pos < this.input.length &&
      !VALUE_STOP_CHARS.has(this.input[this.pos])
    ) {
      this.pos += 1;
    }
    return this.input.slice(start, this.pos).trim();
  }

  parseQuotedValue() {
    this.pos += 1;
    let result = '';
    while (this.pos < this.input.length) {
      if (this.input[this.pos] === '"') {
        if (
          this.pos + 1 < this.input.length &&
          this.input[this.pos + 1] === '"'
        ) {
          result += '"';
          this.pos += 2;
        } else {
          this.pos += 1;
          return result;
        }
      } else {
        result += this.input[this.pos];
        this.pos += 1;
      }
    }
    throw new ParseError(
      'Aspas não fechadas. Valor com aspas não foi terminado',
      this.pos
    );
  }
}
